/**
 * Generative layout parameter search — TestFit-style lock/range optimization.
 *
 * Each layout parameter is either locked (absent from the search space) or
 * given a {min,max,step} range; the search enumerates the bounded cartesian
 * grid IN A FIXED ORDER, evaluates the deterministic parcel test fit for each
 * combination, and ranks candidates lexicographically by yield. The chosen
 * candidate's resolved values are written back into the plain layout
 * configuration by the caller, so frozen snapshots stay ordinary configs and
 * exhibit re-validation is untouched — the search itself is never persisted
 * as an input to the solver.
 */
import type {
  GenerationImpactResult,
  GenerationImpactScenarioInput,
  GenerationLayoutConfiguration,
} from '../../types/generationImpact';
import {
  buildGenerationParcelTestFit,
  type BuildGenerationParcelTestFitInput,
  type GenerationParcelTestFitResult,
} from './generationLayoutGeometry';
import {
  summarizeLayoutYield,
  type GenerationLayoutYieldSummary,
} from './generationLayoutYield';

export interface GenerationLayoutParameterRange {
  min: number;
  max: number;
  step: number;
}

/** Parameters absent from the space stay locked at the base layout's value. */
export interface GenerationLayoutSearchSpace {
  rotationDeg?: GenerationLayoutParameterRange;
  edgeSetbackM?: GenerationLayoutParameterRange;
  componentGapM?: GenerationLayoutParameterRange;
  rowRoadWidthM?: GenerationLayoutParameterRange;
  generationBlockAspectRatio?: GenerationLayoutParameterRange;
}

export type GenerationLayoutSearchParameterKey = keyof GenerationLayoutSearchSpace;

export interface GenerationLayoutSearchCandidate {
  /** Values that differ from the base layout — ready for patchLayout. */
  resolvedValues: Partial<Pick<GenerationLayoutConfiguration, GenerationLayoutSearchParameterKey>>;
  layout: GenerationLayoutConfiguration;
  testFit: GenerationParcelTestFitResult;
  yieldSummary: GenerationLayoutYieldSummary;
  rank: number;
}

export interface GenerationLayoutSearchResult {
  status: 'available' | 'invalid';
  errors: readonly string[];
  candidates: readonly GenerationLayoutSearchCandidate[];
  evaluatedCount: number;
  /** True when the cartesian grid was cut off at the evaluation budget. */
  truncated: boolean;
}

export interface SearchGenerationLayoutsInput {
  base: Omit<BuildGenerationParcelTestFitInput, 'layout'>;
  baseLayout: GenerationLayoutConfiguration;
  space: GenerationLayoutSearchSpace;
  /** Candidates returned, best first. Default 5. */
  topN?: number;
  /** Hard evaluation budget. Default 240. */
  maxEvaluations?: number;
}

const DEFAULT_TOP_N = 5;
const DEFAULT_MAX_EVALUATIONS = 240;
const MAX_VALUES_PER_PARAMETER = 24;
const OVERLAP_EPSILON_ACRES = 1e-6;

const PARAMETER_ORDER: readonly GenerationLayoutSearchParameterKey[] = [
  'rotationDeg',
  'edgeSetbackM',
  'componentGapM',
  'rowRoadWidthM',
  'generationBlockAspectRatio',
];

function rangeValues(
  key: GenerationLayoutSearchParameterKey,
  range: GenerationLayoutParameterRange,
): number[] | string {
  if (!Number.isFinite(range.min) || !Number.isFinite(range.max) || !Number.isFinite(range.step)) {
    return `${key}: min/max/step must be finite`;
  }
  if (range.step <= 0) return `${key}: step must be positive`;
  if (range.max < range.min) return `${key}: max must be >= min`;
  const count = Math.floor((range.max - range.min) / range.step) + 1;
  if (count > MAX_VALUES_PER_PARAMETER) {
    return `${key}: range yields ${count} values; limit is ${MAX_VALUES_PER_PARAMETER} (coarsen the step)`;
  }
  const values: number[] = [];
  for (let index = 0; index < count; index += 1) {
    // Round to 6 decimals so accumulated float steps stay stable across runs.
    values.push(Math.round((range.min + index * range.step) * 1e6) / 1e6);
  }
  return values;
}

/**
 * Lexicographic yield score, lower is better:
 * fit status, blocks contained (desc), exclusion overlap (asc), containment
 * (desc), green reserve (desc). Ties keep enumeration order (stable sort), so
 * earlier grid values — closest to the base layout ordering — win.
 */
function candidateScore(candidate: {
  testFit: GenerationParcelTestFitResult;
  yieldSummary: GenerationLayoutYieldSummary;
}): readonly number[] {
  const { testFit, yieldSummary } = candidate;
  return [
    testFit.status === 'fits' ? 0 : testFit.status === 'partial' ? 1 : 2,
    -yieldSummary.blocksContained,
    testFit.exclusionOverlapAcres == null
      ? (testFit.layout.exclusions?.zones.length ? Number.POSITIVE_INFINITY : 0)
      : Math.max(0, testFit.exclusionOverlapAcres),
    -(testFit.containmentPct ?? 0),
    -(yieldSummary.greenReservePct ?? 0),
  ];
}

function compareScores(left: readonly number[], right: readonly number[]): number {
  for (let index = 0; index < left.length; index += 1) {
    const difference = left[index]! - right[index]!;
    if (Math.abs(difference) > OVERLAP_EPSILON_ACRES) return difference;
  }
  return 0;
}

export function searchGenerationLayouts(
  input: SearchGenerationLayoutsInput,
): GenerationLayoutSearchResult {
  const topN = input.topN ?? DEFAULT_TOP_N;
  const maxEvaluations = input.maxEvaluations ?? DEFAULT_MAX_EVALUATIONS;

  const activeKeys = PARAMETER_ORDER.filter(key => input.space[key] != null);
  if (activeKeys.length === 0) {
    return {
      status: 'invalid',
      errors: ['Enable at least one parameter range to search.'],
      candidates: [],
      evaluatedCount: 0,
      truncated: false,
    };
  }

  const errors: string[] = [];
  const valuesByKey = new Map<GenerationLayoutSearchParameterKey, number[]>();
  for (const key of activeKeys) {
    const values = rangeValues(key, input.space[key]!);
    if (typeof values === 'string') errors.push(values);
    else valuesByKey.set(key, values);
  }
  if (errors.length > 0) {
    return { status: 'invalid', errors, candidates: [], evaluatedCount: 0, truncated: false };
  }

  // Fixed row-major enumeration over the active parameters.
  const combos: Array<Partial<Record<GenerationLayoutSearchParameterKey, number>>> = [{}];
  for (const key of activeKeys) {
    const values = valuesByKey.get(key)!;
    const expanded: typeof combos = [];
    for (const combo of combos) {
      for (const value of values) expanded.push({ ...combo, [key]: value });
    }
    combos.length = 0;
    combos.push(...expanded);
  }
  const truncated = combos.length > maxEvaluations;
  const evaluations = truncated ? combos.slice(0, maxEvaluations) : combos;

  const scored: Array<GenerationLayoutSearchCandidate & { score: readonly number[]; order: number }> = [];
  evaluations.forEach((combo, order) => {
    const layout: GenerationLayoutConfiguration = { ...input.baseLayout, ...combo };
    let testFit: GenerationParcelTestFitResult;
    try {
      testFit = buildGenerationParcelTestFit({ ...input.base, layout });
    } catch {
      return; // out-of-range combination (e.g. setback swallows the envelope)
    }
    const yieldSummary = summarizeLayoutYield(testFit);
    scored.push({
      resolvedValues: combo,
      layout,
      testFit,
      yieldSummary,
      rank: 0,
      score: candidateScore({ testFit, yieldSummary }),
      order,
    });
  });

  scored.sort((left, right) => compareScores(left.score, right.score) || left.order - right.order);
  const candidates = scored.slice(0, topN).map((candidate, index) => ({
    resolvedValues: candidate.resolvedValues,
    layout: candidate.layout,
    testFit: candidate.testFit,
    yieldSummary: candidate.yieldSummary,
    rank: index + 1,
  }));

  return {
    status: 'available',
    errors: [],
    candidates,
    evaluatedCount: scored.length,
    truncated,
  };
}

/** Compact per-alternative parcel-fit verdict for the alternatives matrix. */
export interface AlternativeParcelFitSummary {
  rowId: string;
  status: GenerationParcelTestFitResult['status'];
  blocksContained: number;
  blocksPlaced: number;
  containmentPct: number | null;
  exclusionOverlapAcres: number | null;
}

/**
 * The minimum a row must expose to be parcel-fit ranked: an identity, a block
 * count, the solved footprint, and whatever installed reservations its own
 * project dossier supplies.
 *
 * Deliberately structural rather than tied to `GenerationAlternativeRow`, so
 * the SAME ranker serves both comparison surfaces — the generated alternatives
 * matrix and the saved-scenario comparison — instead of one of them shipping a
 * second, silently divergent containment calculation. `GenerationAlternativeRow`
 * satisfies this shape as-is, so existing callers are unchanged.
 */
export interface GenerationParcelFitCandidate {
  id: string;
  blockCount: number;
  result: { footprint: GenerationImpactResult['footprint'] };
  input: { projectDutyBasis?: GenerationImpactScenarioInput['projectDutyBasis'] };
}

/**
 * Evaluate each candidate against the CURRENT parcel layout —
 * "which fleet variant physically fits this parcel best". Pure projection:
 * per-row failures degrade to an 'indeterminate' verdict, never throw.
 */
export function rankAlternativeParcelFits(input: {
  rows: readonly GenerationParcelFitCandidate[];
  base: Omit<
    BuildGenerationParcelTestFitInput,
    'layout' | 'generationBlockCount' | 'footprint' | 'installedReservations'
  >;
  layout: GenerationLayoutConfiguration;
}): ReadonlyMap<string, AlternativeParcelFitSummary> {
  const summaries = new Map<string, AlternativeParcelFitSummary>();
  for (const row of input.rows) {
    try {
      const installedReservations = row.input.projectDutyBasis?.installedReservations;
      const testFit = buildGenerationParcelTestFit({
        ...input.base,
        layout: input.layout,
        generationBlockCount: Math.max(1, row.blockCount),
        footprint: row.result.footprint,
        // The solver requires reservation block quantity == block count; rows
        // resized away from the duty dossier fall back to planning acreage.
        ...(installedReservations && installedReservations.blockQuantity === row.blockCount
          ? { installedReservations }
          : {}),
      });
      const yieldSummary = summarizeLayoutYield(testFit);
      summaries.set(row.id, {
        rowId: row.id,
        status: testFit.status,
        blocksContained: yieldSummary.blocksContained,
        blocksPlaced: yieldSummary.blocksPlaced,
        containmentPct: testFit.containmentPct,
        exclusionOverlapAcres: testFit.exclusionOverlapAcres ?? null,
      });
    } catch {
      summaries.set(row.id, {
        rowId: row.id,
        status: 'indeterminate',
        blocksContained: 0,
        blocksPlaced: 0,
        containmentPct: null,
        exclusionOverlapAcres: null,
      });
    }
  }
  return summaries;
}
